import { ReportDocument } from "@/components/report-print/ReportDocument";
import type {
  ReportColumn,
  ReportDocumentModel,
  ReportTableRow,
} from "@/components/report-print/reportTypes";
import type {
  CaixasExportDocument,
  CaixasExportMovement,
} from "@/features/supplier/types";

interface CaixasPrintDocumentProps {
  document: CaixasExportDocument;
  printMode?: "source" | "preview";
}

const MOVEMENT_COLUMNS: ReportColumn[] = [
  { key: "date", label: "Data", width: "13%" },
  { key: "operation", label: "Movimento", width: "17%" },
  { key: "reference", label: "Referência", width: "26%" },
  {
    key: "quantity",
    label: "Qtd.",
    width: "12%",
    align: "right",
    noWrap: true,
  },
  {
    key: "balance",
    label: "Saldo",
    width: "12%",
    align: "right",
    noWrap: true,
  },
  { key: "observation", label: "Obs.", width: "20%" },
];

const HIGHLIGHT_COLUMNS: ReportColumn[] = [
  { key: "movement", label: "Movimento", width: "45%" },
  { key: "date", label: "Data", width: "25%", noWrap: true },
  {
    key: "quantity",
    label: "Qtd.",
    width: "30%",
    align: "right",
    noWrap: true,
  },
];

export function buildCaixasReportModel(
  document: CaixasExportDocument,
): ReportDocumentModel {
  const metrics = [
    { label: "Emprestadas", value: String(document.total_lent) },
    { label: "Devolvidas", value: String(document.total_returned) },
    {
      label: "Saldo",
      value: String(document.balance),
      tone: "highlight" as const,
    },
    { label: "Pedidos", value: String(document.total_orders) },
    { label: "Devoluções", value: String(document.total_returns) },
  ];

  return {
    header: {
      brand: "EloLink",
      logoSvg: document.logo_svg,
      documentLabel: "Controle de Caixas",
      title: document.supplier.name,
      subtitle: `${document.period_start} a ${document.period_end}`,
      dateLabel: document.date,
      dateSubLabel: document.weekday,
    },
    metrics,
    metricRows: [metrics],
    sections: [
      {
        id: "caixas-movements",
        title: "Movimentos de caixas",
        subtitle: "Empréstimos e devoluções no período",
        columns: MOVEMENT_COLUMNS,
        rows: buildMovementRows(document.movements),
      },
      {
        id: "caixas-highlights",
        title: "Últimos movimentos",
        subtitle: "Movimentos mais recentes do período",
        columns: HIGHLIGHT_COLUMNS,
        rows: [
          {
            id: "caixas-last-loan",
            cells: [
              { columnKey: "movement", value: "Último empréstimo" },
              { columnKey: "date", value: document.last_loan.date },
              {
                columnKey: "quantity",
                value: String(document.last_loan.quantity),
                align: "right" as const,
              },
            ],
          },
          {
            id: "caixas-last-return",
            cells: [
              { columnKey: "movement", value: "Última devolução" },
              { columnKey: "date", value: document.last_return.date },
              {
                columnKey: "quantity",
                value: String(document.last_return.quantity),
                align: "right" as const,
              },
            ],
          },
        ],
      },
    ],
    footer: {
      brand: document.responsible.name || "EloLink",
      generatedLabel: `Gerado em ${document.generated_at}`,
      documentNumber: document.number,
    },
    continuationContext: `Controle de Caixas · ${document.supplier.name}`,
  };
}

export function CaixasPrintDocument({
  document,
  printMode = "source",
}: CaixasPrintDocumentProps) {
  return (
    <ReportDocument
      document={buildCaixasReportModel(document)}
      printMode={printMode}
    />
  );
}

function buildMovementRows(
  movements: CaixasExportMovement[],
): ReportTableRow[] {
  if (movements.length === 0) {
    return [
      {
        id: "caixas-movements-empty",
        tone: "muted",
        cells: [
          {
            columnKey: "reference",
            value: "Nenhum movimento no período.",
          },
        ],
      },
    ];
  }

  return movements.map((movement, index) => ({
    id: `caixas-movement-${index}`,
    tone: movement.current ? ("highlight" as const) : ("default" as const),
    cells: [
      { columnKey: "date", value: movement.date },
      { columnKey: "operation", value: movementOperation(movement) },
      { columnKey: "reference", value: movement.reference },
      {
        columnKey: "quantity",
        value: movementQuantity(movement),
        align: "right" as const,
      },
      {
        columnKey: "balance",
        value: String(movement.balance),
        align: "right" as const,
        emphasis: true,
      },
      { columnKey: "observation", value: movement.observation },
    ],
  }));
}

function movementOperation(movement: CaixasExportMovement) {
  if (movement.lent > 0 && movement.returned > 0) return "Ajuste";
  if (movement.lent > 0) return "Empréstimo";
  if (movement.returned > 0) return "Devolução";
  return "Saldo";
}

function movementQuantity(movement: CaixasExportMovement) {
  if (movement.lent > 0 && movement.returned > 0) {
    return `${movement.lent}/${movement.returned}`;
  }
  return String(movement.lent > 0 ? movement.lent : movement.returned);
}
